(function () {
    const STORAGE_KEYS = {
        cartAttributeApplied: 'custom_cart_discount_cart_attribute_applied',
        activatedAt: 'custom_cart_discount_activated_at',
        lastActivityAt: 'custom_cart_discount_last_activity_at'
    };

    const COOKIE_KEYS = {
        activeSession: 'custom_cart_discount_active_session'
    };

    const CART_ATTRIBUTES = {
        qrDiscount: {
            key: 'campaign_qr_discount',
            value: 'eligible'
        },
        campaignName: {
            key: 'campaign_name'
        }
    };

    const UPDATE_CART_ENDPOINT = '/cart/update.js';

    const REQUEST_HEADERS = {
        'Content-Type': 'application/json',
        Accept: 'application/json'
    };

    const INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000;
    const MAX_SESSION_TTL_MS = 4 * 60 * 60 * 1000;
    const ACTIVITY_THROTTLE_MS = 10 * 1000;
    const config = window.customCartDiscount || {};
    const currentTemplate = config.currentTemplate;
    const currentPageHandle = config.currentPageHandle;
    const configuredDiscounts = Array.isArray(config.discounts) ? config.discounts : [];
    let hasRefreshedCartDrawerOnLoad = false;

    if (!configuredDiscounts.length) {

        return;
    }

    function parseConfiguredParams(rawParams) {
        if (typeof rawParams !== 'string') {

            return [];
        }

        const normalized = rawParams.trim().replace(/^\?/, '');

        if (!normalized || !normalized.includes('=')) {

            return [];
        }

        const params = new URLSearchParams(normalized);
        const parsedPairs = [];

        params.forEach(function (value, key) {
            if (!key) {

                return;
            }

            parsedPairs.push({ key: key.trim(), value: value.trim() });
        });

        return parsedPairs;
    }

    function normalizeParamKey(value) {
        if (typeof value !== 'string') {
            return '';
        }

        return value.trim().toLowerCase();
    }

    function normalizeParamValue(value) {
        if (typeof value !== 'string') {
            return '';
        }

        return value
            .replace(/\+/g, ' ')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    function doesCurrentUrlMatchRequiredParams(requiredPairs) {
        if (!requiredPairs.length) {

            return false;
        }

        const currentParams = new URLSearchParams(window.location.search);
        const normalizedCurrentParams = new Map();

        currentParams.forEach(function (value, key) {
            const normalizedKey = normalizeParamKey(key);

            if (!normalizedCurrentParams.has(normalizedKey)) {
                normalizedCurrentParams.set(normalizedKey, value);
            }
        });

        const hasAllPairs = requiredPairs.every(function (pair) {
            const pairKey = normalizeParamKey(pair.key);
            const pairValue = normalizeParamValue(pair.value);
            const currentValue = normalizedCurrentParams.get(pairKey) ?? null;

            if (currentValue === null) {
                return false;
            }

            return normalizeParamValue(currentValue) === pairValue;
        });

        return hasAllPairs;
    }

    function doesDiscountPageMatch(discount) {
        const pageType = discount.pageType;

        if (pageType === 'homepage') {
            return currentTemplate === 'index';
        }

        if (pageType === 'other') {
            const pageHandle = discount.pageHandle;

            if (!pageHandle) {
                return false;
            }

            return pageHandle === currentPageHandle;
        }

        return false;
    }

    function getMatchingDiscount() {
        for (let i = 0; i < configuredDiscounts.length; i += 1) {
            const discount = configuredDiscounts[i];
            const urlParams = discount.urlParams;
            const campaignName = discount.campaignName;

            if (!urlParams || !campaignName) {
                continue;
            }

            if (!doesDiscountPageMatch(discount)) {
                continue;
            }

            const requiredPairs = parseConfiguredParams(urlParams);

            if (!doesCurrentUrlMatchRequiredParams(requiredPairs)) {
                continue;
            }

            return {
                campaignName: campaignName
            };
        }

        return null;
    }

    function setSessionCookie(name, value) {
        document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; SameSite=Lax';
    }

    function getCookie(name) {
        const encodedName = name + '=';
        const cookieParts = document.cookie.split(';');

        for (let i = 0; i < cookieParts.length; i += 1) {
            const cookie = cookieParts[i].trim();

            if (cookie.indexOf(encodedName) === 0) {
                return decodeURIComponent(cookie.substring(encodedName.length));
            }
        }

        return null;
    }

    function writeSessionState(isActive) {
        if (isActive) {
            setSessionCookie(COOKIE_KEYS.activeSession, '1');

            return;
        }

        document.cookie = COOKIE_KEYS.activeSession + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
    }

    function hasActiveSession() {
        const activeFlag = getCookie(COOKIE_KEYS.activeSession);

        return activeFlag === '1';
    }

    function markAttributeApplied(isApplied) {
        if (isApplied) {
            localStorage.setItem(STORAGE_KEYS.cartAttributeApplied, '1');

            return;
        }

        localStorage.removeItem(STORAGE_KEYS.cartAttributeApplied);
    }

    function wasAttributeApplied() {
        const appliedFlag = localStorage.getItem(STORAGE_KEYS.cartAttributeApplied);
        return appliedFlag === '1';
    }

    function getNumberFromStorage(key) {
        const rawValue = localStorage.getItem(key);

        if (!rawValue) {

            return null;
        }

        const parsedValue = Number(rawValue);
        if (Number.isNaN(parsedValue)) {
            return null;
        }

        return parsedValue;
    }

    function setActivationTimestamps(nowTimestamp) {
        localStorage.setItem(STORAGE_KEYS.activatedAt, String(nowTimestamp));
        localStorage.setItem(STORAGE_KEYS.lastActivityAt, String(nowTimestamp));
    }

    function clearActivationTimestamps() {
        localStorage.removeItem(STORAGE_KEYS.activatedAt);
        localStorage.removeItem(STORAGE_KEYS.lastActivityAt);
    }

    function updateLastActivity(nowTimestamp) {
        localStorage.setItem(STORAGE_KEYS.lastActivityAt, String(nowTimestamp));
    }

    function hasCampaignSessionExpired(nowTimestamp) {
        const activatedAt = getNumberFromStorage(STORAGE_KEYS.activatedAt);
        const lastActivityAt = getNumberFromStorage(STORAGE_KEYS.lastActivityAt);

        if (!activatedAt || !lastActivityAt) {
            return true;
        }

        const hasPassedMaxSessionTtl = nowTimestamp - activatedAt > MAX_SESSION_TTL_MS;

        if (hasPassedMaxSessionTtl) {
            return true;
        }

        const hasPassedInactivityTimeout = nowTimestamp - lastActivityAt > INACTIVITY_TIMEOUT_MS;
        return hasPassedInactivityTimeout;
    }

    async function updateCartAttributes(qrDiscountValue, campaignNameValue, keepalive) {
        try {
            const response = await fetch(UPDATE_CART_ENDPOINT, {
                method: 'POST',
                headers: REQUEST_HEADERS,
                body: JSON.stringify({
                    attributes: {
                        [CART_ATTRIBUTES.qrDiscount.key]: qrDiscountValue,
                        [CART_ATTRIBUTES.campaignName.key]: campaignNameValue
                    }
                }),
                credentials: 'same-origin',
                keepalive: Boolean(keepalive)
            });

            if (!response.ok) {
                console.error('[custom-cart-discount] Failed cart attribute update', response.status);
                return false;
            }

            return true;
        } catch (error) {
            console.error('[custom-cart-discount] Cart attribute request error', error);

            return false;
        }
    }

    async function addDiscountAttributes(campaignName) {
        const didApply = await updateCartAttributes(
            CART_ATTRIBUTES.qrDiscount.value,
            campaignName,
            false
        );

        if (!didApply) {
            return;
        }

        markAttributeApplied(true);
    }

    async function removeDiscountAttributes(useKeepalive) {
        const didRemove = await updateCartAttributes('', '', useKeepalive);

        if (!didRemove) {
            return;
        }

        markAttributeApplied(false);
    }

    async function refreshCartDrawerOnLoad() {
        if (hasRefreshedCartDrawerOnLoad) {
            return;
        }

        const cartModal = document.querySelector('cart-modal');

        if (!cartModal || typeof cartModal.reloadContent !== 'function') {
            return;
        }

        hasRefreshedCartDrawerOnLoad = true;

        console.log('refreshCartDrawerOnLoad');
        await cartModal.reloadContent();
    }

    async function clearCampaignSession(removeAttribute, refreshDrawerOnLoad) {
        writeSessionState(false);
        clearActivationTimestamps();

        if (!wasAttributeApplied()) {
            markAttributeApplied(false);
            return;
        }

        if (!removeAttribute) {
            return;
        }

        await removeDiscountAttributes(false);

        if (refreshDrawerOnLoad) {
            await refreshCartDrawerOnLoad();
        }
    }

    const matchingDiscount = getMatchingDiscount();
    const shouldActivateDiscountSession = Boolean(matchingDiscount);
    const nowTimestamp = Date.now();

    if (hasActiveSession() && hasCampaignSessionExpired(nowTimestamp)) {
        void clearCampaignSession(true, true);
    }

    if (shouldActivateDiscountSession) {
        writeSessionState(true);
        setActivationTimestamps(nowTimestamp);

        const shouldRefreshDrawer = !wasAttributeApplied();

        void addDiscountAttributes(matchingDiscount.campaignName).then(function () {
            if (shouldRefreshDrawer) {
                void refreshCartDrawerOnLoad();
            }
        });
    } else if (!hasActiveSession() && wasAttributeApplied()) {
        void clearCampaignSession(true, true);
    }

    let lastActivityWriteAt = 0;

    function refreshActivityTimestamp(forceWrite) {
        if (!hasActiveSession()) {
            return;
        }

        const currentTimestamp = Date.now();

        if (!forceWrite && currentTimestamp - lastActivityWriteAt < ACTIVITY_THROTTLE_MS) {
            return;
        }

        if (hasCampaignSessionExpired(currentTimestamp)) {
            void clearCampaignSession(true, false);
            return;
        }

        lastActivityWriteAt = currentTimestamp;
        updateLastActivity(currentTimestamp);
    }

    document.addEventListener('pointerdown', function () {
        refreshActivityTimestamp(false);
    }, { passive: true });

    document.addEventListener('keydown', function () {
        refreshActivityTimestamp(false);
    }, { passive: true });

    document.addEventListener('scroll', function () {
        refreshActivityTimestamp(false);
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState !== 'visible') {
            return;
        }

        refreshActivityTimestamp(true);
    });

    window.addEventListener('focus', function () {
        refreshActivityTimestamp(true);
    });

    window.addEventListener('storage', function (event) {
        if (event.key !== STORAGE_KEYS.lastActivityAt && event.key !== STORAGE_KEYS.activatedAt) {
            return;
        }

        if (!hasActiveSession()) {
            return;
        }

        const currentTimestamp = Date.now();

        if (hasCampaignSessionExpired(currentTimestamp)) {
            void clearCampaignSession(true, false);
        }
    });

    if (hasActiveSession()) {
        refreshActivityTimestamp(true);
    }

    console.log('custom-cart-discount.js loaded');
})();
